import { Box, Link, Typography } from "@mui/material";
import type { ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { colors, alphaVar } from "../../../components/ui/colors";

const getSectionChipStyles = (children: ReactNode) => {
  const label = String(children).toLowerCase();

  if (label.includes("fixed")) {
    return {
      borderColor: alphaVar(colors.success.base, 0.18),
      backgroundColor: alphaVar(colors.success.base, 0.1),
      color: colors.success.base,
    };
  }

  if (label.includes("improved")) {
    return {
      borderColor: alphaVar(colors.info.base, 0.18),
      backgroundColor: alphaVar(colors.info.base, 0.1),
      color: colors.info.base,
    };
  }

  return {
    borderColor: alphaVar(colors.orange.base, 0.18),
    backgroundColor: alphaVar(colors.orange.base, 0.1),
    color: colors.orange.base,
  };
};

const markdownComponents: Components = {
  h2: ({ children }) => {
    const chipStyles = getSectionChipStyles(children);

    return (
    <Typography
      component="div"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        mt: 1.5,
        mb: 0.75,
        px: 0.85,
        py: 0.35,
        borderRadius: 999,
        border: "1px solid",
        fontSize: "0.62rem",
        fontWeight: 800,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        lineHeight: 1,
        ...chipStyles,
      }}
    >
      {children}
    </Typography>
    );
  },
  h3: ({ children }) => (
    <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1.5, color: colors.gray.base }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body2" sx={{ color: colors.gray[400], lineHeight: 1.55, my: 0.45 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box
      component="ul"
      sx={{
        pl: 0,
        my: 0.35,
        listStyle: "none",
      }}
    >
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography
      component="li"
      variant="body2"
      sx={{
        position: "relative",
        color: colors.gray.base,
        lineHeight: 1.55,
        mb: 0.7,
        pl: 2,
        "&::before": {
          content: '"—"',
          position: "absolute",
          left: 0,
          color: colors.orange.base,
          fontWeight: 800,
        },
      }}
    >
      {children}
    </Typography>
  ),
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener" sx={{ color: colors.orange[700], fontWeight: 600 }}>
      {children}
    </Link>
  ),
  strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>,
  code: ({ children }) => (
    <Box
      component="code"
      sx={{
        fontFamily: "monospace",
        fontSize: "0.85em",
        px: 0.6,
        py: 0.1,
        borderRadius: 1,
        backgroundColor: colors.orange[50],
        color: colors.orange[900],
      }}
    >
      {children}
    </Box>
  ),
};

interface ReleaseMarkdownProps {
  children?: ReactNode;
}

export default function ReleaseMarkdown({ children }: ReleaseMarkdownProps) {
  return <ReactMarkdown components={markdownComponents}>{typeof children === "string" ? children : ""}</ReactMarkdown>;
}
